import type { Country } from '@/types/country.types';

import i18n from '@/dependencies/i18n';
import numberService from '@/services/number.service';
import { defineRule } from 'vee-validate';
import { email, max, max_value, min, min_value, numeric, required } from '@vee-validate/rules';
import { isValidIBANNumber } from '@/helpers/utility.helper';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { apiClient } from '@/core/api';
import { debounce } from 'lodash';

const { t } = i18n.global;

defineRule('emptyArray', (value?: string) => {
    if (value && value.length === 0) {
        return t('VALIDATION.THIS_FIELD_IS_REQUIRED');
    }

    return true;
});

defineRule('required', (value?: unknown) => {
    if (typeof value !== 'boolean' && !required(value)) {
        return t('VALIDATION.THIS_FIELD_IS_REQUIRED');
    }

    return true;
});

defineRule('email', (value: unknown) => {
    if (!email(value)) {
        return t('VALIDATION.THIS_FIELD_MUST_BE_A_VALID_EMAIL');
    }

    return true;
});

defineRule('confirmed', (value: unknown, [target]: any, ctx) => {
    if (value === ctx.form[target]) {
        return true;
    }

    return t('VALIDATION.THIS_FIELD_CONFIRMATION_DOES_NOT_MATCH');
});

defineRule('kvk', (value?: string) => {
    if (!value) {
        return true;
    }

    const regex = /^[0-9]{8}$/;

    if (value && value.match(regex)) {
        return true;
    }

    return t('VALIDATION.VALID_KVK_NUMBER');
});

// KvkComboBox binds a `{ name, number }` object, so the plain `required` rule (which passes for
// any non-empty object, including `{}`) can't validate it. `kvkRequired` mirrors the component's
// former hand-rolled check (name OR number present); `kvkComplete` demands a full selection (both).
defineRule('kvkRequired', (value?: { name?: string; number?: string }) => {
    if (value && (value.name || value.number)) {
        return true;
    }

    return t('VALIDATION.THIS_FIELD_IS_REQUIRED');
});

defineRule('kvkComplete', (value?: { name?: string; number?: string }) => {
    if (value && value.name && value.number) {
        return true;
    }

    return t('VALIDATION.SELECT_KVK_COMPANY');
});

defineRule('isTrue', (value: unknown) => {
    if (value === true) {
        return true;
    }

    return t('FUNNEL.GLOBAL.NOT_POSSIBLE');
});

defineRule('isFalse', (value) => {
    if (value === false) {
        return true;
    }

    return t('FUNNEL.GLOBAL.NOT_POSSIBLE');
});

defineRule('highRiskCountry', (value: any) => {
    if (value.category <= 4) {
        return true;
    }

    return t('VALIDATION.NOT_POSSIBLE');
});

defineRule('currency', (value: string) => {
    const regex = /^(\d+(?:[.,]\d{1,2})?|)$/;

    if (regex.test(value)) {
        return true;
    }

    return t('VALIDATION.VALID_CURRENCY');
});

defineRule('only_true', (value: unknown) => {
    if (value === true) {
        return true;
    }

    return t('VALIDATION.THIS_FIELD_IS_REQUIRED');
});

defineRule('url', (value: string) => {
    const regex = /(http(s)?:\/\/.)?[-a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_.~#?&//=]*)/g;

    if (!value) {
        return true;
    }

    if (regex.test(value)) {
        return true;
    }

    return t('VALIDATION.VALID_URL');
});

defineRule('numeric', (value: unknown) => {
    if (!numeric(value)) {
        return t('VALIDATION.ONLY_NUMBERS');
    }

    return true;
});

defineRule('min_value', (value: string, [limit]) => {
    const newValue = numberService.stringToFloat(value);
    limit = numberService.stringToFloat(limit);

    if (min_value(newValue, { min: limit })) {
        return true;
    }

    return t('VALIDATION.MIN_AMOUNT', { value: limit?.toString() ?? 0 });
});

defineRule('max_value', (value: string, [limit]) => {
    const newValue = numberService.stringToFloat(value);
    limit = numberService.stringToFloat(limit);

    if (max_value(newValue, { max: limit })) {
        return true;
    }

    return t('VALIDATION.MAX_AMOUNT', { value: limit?.toString() ?? 0 });
});

defineRule('phone', (value: unknown, [target]: any, ctx) => {
    const countryCode = (ctx.form[target] as Country | undefined)?.dialCode ?? '31';

    const phoneNumber = parsePhoneNumberFromString('+' + countryCode + value);

    if (phoneNumber?.isValid()) {
        return true;
    }

    return t('VALIDATION.VALID_PHONE_NUMBER');
});

defineRule('phonenumber', (value) => {
    if (isValidPhoneNumber(value, 'NL')) {
        return true;
    }
    return t('VALIDATION.VALID_PHONE_NUMBER');
});

// Validates a full E.164 number (e.g. "+31612345678"), as emitted by PhoneInput.
// Empty is allowed so `required` owns presence; the dial code is carried in the value itself.
defineRule('intlPhone', (value: unknown) => {
    if (!value) {
        return true;
    }
    if (isValidPhoneNumber(String(value))) {
        return true;
    }
    return t('VALIDATION.VALID_PHONE_NUMBER');
});

defineRule('min', (value: unknown, [length]) => {
    if (min(value, { length })) {
        return true;
    }

    return t('VALIDATION.MIN_LENGTH_AMOUNT', { length: length?.toString() ?? 0 });
});

defineRule('max', (value: unknown, [length]) => {
    if (max(value, { length })) {
        return true;
    }

    return t('VALIDATION.MAX_LENGTH_AMOUNT', { length: length?.toString() });
});

defineRule('atLeastOneDirector', (_value: unknown, [param]) => {
    if (param === 'false') {
        return t('VALIDATION.SELECT_DIRECTORS');
    }

    return true;
});

defineRule('zipcode', (value: string) => {
    const regex = /^[1-9][0-9]{3}[ -\\.]?[A-Za-z]{2}$/;

    if (value?.match(regex)) {
        return true;
    }

    return t('VALIDATION.VALID_ZIPCODE');
});

defineRule('noBvOnName', (value: string) => {
    const regex = /\bb\.?v\.?\b/gi;

    if (value.toLocaleLowerCase().search(regex) === -1) {
        return true;
    }

    return t('FUNNEL.GLOBAL.COMPANY_NAME_CONTAINS', { suffix: 'BV' });
});

defineRule('correctAge', (value: Date | number | undefined) => {
    if (value === undefined) return t('VALIDATION.CORRECT_AGE');

    if (typeof value === 'number') {
        return value >= 18 && value <= 70 ? true : t('VALIDATION.CORRECT_AGE');
    }

    const today = new Date();
    const min = new Date(today.getFullYear() - 70, today.getMonth(), today.getDate());
    const max = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

    if (value < max && value > min) {
        return true;
    }

    return t('VALIDATION.CORRECT_AGE');
});

defineRule('iban', (value: string) => {
    if (isValidIBANNumber(value)) {
        return true;
    }

    return t('VALIDATION.VALID_IBAN');
});

defineRule('files', (value: File[]) => {
    if (value.length > 0) {
        return true;
    }

    return t('VALIDATION.NO_FILE_SELECTED');
});

defineRule('json', (value: string) => {
    try {
        JSON.parse(value as string);
        return true;
    } catch (e) {
        return `Invalid JSON: ${e}`;
    }
});

defineRule('notEmptyObject', (value: object) => {
    return Object.keys(value).length > 0 ? true : t('VALIDATION.THIS_FIELD_IS_REQUIRED');
});

const checkEmail = debounce(async (email: string) => await apiClient.utils.email[email].$get(), 100);

defineRule('validEmailDomain', async (value: string) => {
    const response = await checkEmail(value);
    if (!response) return true;

    return response.valid ? true : t('VALIDATION.THIS_FIELD_MUST_BE_A_VALID_EMAIL');
});

defineRule('vat', (value: string) => {
    return /^(NL)?[0-9]{9}B[0-9]{2}$/.test(value) || t('VALIDATION.VAT');
});
